import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import { DISPLAY_BY } from 'utils/constants';
import { IDisplayByType, IHeadcountSpend } from 'utils/types';

const OTHERS = 'Others';

export const getCategories = ({
  displayBy,
  headcountSpend,
}: {
  displayBy: IDisplayByType;
  headcountSpend: IHeadcountSpend[];
}) => {
  const orderProp =
    displayBy === DISPLAY_BY.HEADCOUNT ? 'numberOfEmployees' : 'cost';
  let categories = [];
  const orderedItems = orderBy(headcountSpend, orderProp, 'desc');
  categories = uniq(orderedItems.map((item) => item.clientRole));
  if (categories.length > 20) {
    const topCategories = categories.splice(0, 19);
    return {
      categories: [...topCategories, OTHERS],
      others: categories,
    };
  }
  return { categories, others: [] };
};

export const getOthersValue = ({
  displayBy,
  items,
  others,
  revenue,
}: {
  displayBy: IDisplayByType;
  items: IHeadcountSpend[];
  others: string[];
  revenue: number;
}) => {
  return others.reduce((acc, otherCategory) => {
    const item = items.find((it) => it.clientRole === otherCategory);
    return acc + getItemDataValue({ item, displayBy, revenue });
  }, 0);
};

export const getData = ({
  displayBy,
  headcountSpend,
  permanentContractorChecked,
  revenue,
}: {
  displayBy: IDisplayByType;
  headcountSpend: IHeadcountSpend[];
  permanentContractorChecked?: boolean;
  revenue: number;
}) => {
  const { categories, others } = getCategories({ displayBy, headcountSpend });
  let series = [];
  if (permanentContractorChecked) {
    const groupedFilteredItems = groupBy(headcountSpend, 'permanentOrContract');
    series = Object.keys(groupedFilteredItems).map((key) => ({
      name: key,
      data: categories.map((category) => {
        const item = groupedFilteredItems[key].find(
          (it) => it.clientRole === category
        );
        if (category === OTHERS) {
          return getOthersValue({
            displayBy,
            items: groupedFilteredItems[key],
            others,
            revenue,
          });
        }
        return getItemDataValue({ item, displayBy, revenue });
      }),
    }));
  } else {
    series = [
      {
        data: categories.map((category) => {
          const item = headcountSpend.find((it) => it.clientRole === category);
          if (category === OTHERS) {
            return getOthersValue({
              displayBy,
              items: headcountSpend,
              others,
              revenue,
            });
          }
          return getItemDataValue({ item, displayBy, revenue });
        }),
      },
    ];
  }
  return { categories, others, series };
};

export const getItemDataValue = ({
  item,
  displayBy,
  revenue,
}: {
  item?: IHeadcountSpend;
  displayBy: IDisplayByType;
  revenue: number;
}) => {
  if (displayBy === DISPLAY_BY.PERCENTAGE_OF_REVENUE) {
    return ((item ? item.cost : 0) / (revenue || 1)) * 100;
  }
  if (displayBy === DISPLAY_BY.HEADCOUNT) {
    return item && item.count ? item.count : 0;
  }
  return item ? item.cost : 0;
};
